import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getDeviceInfo } from '../utils';
import { v4 as uuidv4 } from 'uuid';
import { APP_NAME } from '../config/constants';
import { useAppSettingsStore } from '../lib';
import {
  isAuthenticationError,
  isRegistrationAuthenticationError,
  useSessionStore,
} from '@/entities/session';
import packageJson from '../../../package.json';
import {
  checkSystemHealth,
  useSystemStatusStore,
  type SystemHealthResponse,
} from '@/entities/system';
import { useRegistrationStore } from '@/entities/registration';

const API_BASE_URL = '/api';

let healthCheckPromise: Promise<SystemHealthResponse> | null = null;

const runHealthCheck = async () => {
  if (!healthCheckPromise) {
    healthCheckPromise = checkSystemHealth().finally(() => {
      healthCheckPromise = null;
    });
  }

  return healthCheckPromise;
};

const sharedRequestInterceptor = (config: InternalAxiosRequestConfig) => {
  if (!config.headers.Authorization) {
    const accessToken = useSessionStore.getState().getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      delete config.headers.Authorization;
    }
  }

  const theme = useAppSettingsStore.getState().theme;
  const language = useAppSettingsStore.getState().language;
  const deviceId = useAppSettingsStore.getState().deviceId;
  const appVersion = packageJson.version;

  const { os, osVersion, model } = getDeviceInfo();

  config.headers['f2x_request_id'] = uuidv4();
  config.headers['f2x_user_agent'] =
    `app=${APP_NAME}; app_v=${appVersion}; os=${os}; os_v=${osVersion}; model=${model}; deviceId=${deviceId}; lang=${language}; theme=${theme}; channel=WebPA`;

  return config;
};

const createClient = (timeout: number): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout,
    withCredentials: true,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(sharedRequestInterceptor);

  return client;
};

const apiClient = createClient(30000);
const healthClient = createClient(10000);

apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const status = error.response?.status;

    const isTimeout =
      error.code === 'ECONNABORTED' || error.message.includes('timeout');

    const isServerError = (status ?? 0) >= 500;

    const isNetworkError = !error.response && !!error.request;

    if (
      useSystemStatusStore.getState().status === 'timeout' ||
      useSystemStatusStore.getState().status === 'maintenance'
    ) {
      return Promise.reject(error);
    }

    if (
      (isTimeout || isNetworkError || isServerError) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      if (useSystemStatusStore.getState().status !== 'checking') {
        useSystemStatusStore.getState().setStatus('checking');
      }

      try {
        const health = await runHealthCheck();

        if ('msg' in health) {
          useSystemStatusStore.getState().setStatus('maintenance', health);

          return Promise.reject(error);
        }

        if ('timeoutServer' in health) {
          useSystemStatusStore.getState().setStatus('ok');

          return apiClient(originalRequest);
        }
        useSystemStatusStore.getState().setStatus('timeout');

        return Promise.reject(error);
      } catch {
        useSystemStatusStore.getState().setStatus('timeout');

        return Promise.reject(error);
      }
    }

    if (isRegistrationAuthenticationError(error, originalRequest.url ?? '')) {
      try {
        const updatedRequest = await useRegistrationStore
          .getState()
          .handleRegistrationStateRefresh(originalRequest);
        return apiClient(updatedRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    if (isAuthenticationError(error) && !originalRequest._retry) {
      try {
        const updatedRequest = await useSessionStore
          .getState()
          .handleTokenRefresh(originalRequest);
        return apiClient(updatedRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient, healthClient };
